import { Company } from "types/crm/company";
import { ContactOwner } from "types/crm/contacts";
import {
  CONTACT_IMPORT_FORMATS,
  CONTACT_IMPORT_MAX_FILE_SIZE_BYTES,
  CONTACT_IMPORT_MAX_FILE_SIZE_MB,
  CONTACT_IMPORT_MAX_ROWS,
  CONTACT_IMPORT_NO_FIELD,
  CONTACT_IMPORT_NONE,
  CONTACT_IMPORT_NO_VALUE,
  CONTACT_IMPORT_REQUIRED_FIELDS,
  CONTACT_IMPORT_TARGET_FIELDS,
  CONTACT_IMPORT_UNSET_FIELD,
  ContactImportColumnInfo,
  ContactImportColumnMapping,
  ContactImportCompanyMapping,
  ContactImportCompanyMatch,
  ContactImportCompanySuggestion,
  ContactImportDoNotImportValue,
  ContactImportDropdownChoice,
  ContactImportDropdownColumn,
  ContactImportDropdownField,
  ContactImportDropdownOption,
  ContactImportDropdownValue,
  ContactImportFieldKey,
  ContactImportOwnerMapping,
  ContactImportReviewSummary,
  ContactImportFieldMap,
  ContactImportHeaderAnalysis,
  ContactImportIncompatibility,
  ContactImportTargetField,
  ContactImportUpload,
  ContactImportValueMappingRequest,
  ContactImportValueMappingType,
  ContactImportFormat,
  ContactImportRawRow,
  ParsedContactFile,
} from "types/crm/contactImport";

/**
 * Reading and parsing an uploaded contacts file into raw rows.
 *
 * Field mapping and validation deliberately live outside this module — the
 * output here is the untouched spreadsheet content keyed by its own headers,
 * so later wizard steps can map and validate it independently.
 */

/**
 * Dev-only trace of what each step of the wizard sent and got back.
 *
 * One entry per side of a call, labelled by the step it belongs to, so the
 * console reads as the order the flow actually happened in. Stripped in
 * production: an import payload carries contact data.
 */
export const logContactImportStep = (
  step: string,
  phase: "request" | "response" | "error",
  payload?: unknown
): void => {
  if (process.env.NODE_ENV === "production") return;

  // eslint-disable-next-line no-console
  const write = phase === "error" ? console.error : console.log;
  write(`[CRM Import] ${step} · ${phase}`, payload);
};

/** A parse failure we can explain to the user, as opposed to an unexpected bug. */
export class ContactImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ContactImportError";
    // Required for `instanceof` to work when targeting ES5.
    Object.setPrototypeOf(this, ContactImportError.prototype);
  }
}

/** Header names that would corrupt the row object's prototype if used as keys. */
const UNSAFE_HEADERS = ["__proto__", "constructor", "prototype"];

const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex === -1 ? "" : fileName.slice(dotIndex).toLowerCase();
};

/** Resolves a file name to one of the supported formats, or null when unsupported. */
export const getContactImportFormat = (
  fileName: string
): ContactImportFormat | null =>
  CONTACT_IMPORT_FORMATS.find(
    (format) => format.extension === getFileExtension(fileName)
  ) ?? null;

/** Bytes as MB, for the size error message. */
const toMegabytes = (bytes: number): string => (bytes / 1024 / 1024).toFixed(1);

/**
 * Cells arrive as strings from CSV but as numbers/dates/booleans from XLSX,
 * so everything is flattened to trimmed text and interpreted later.
 */
const toCellText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  return String(value).trim();
};

const isBlankRow = (cells: unknown[]): boolean =>
  cells.every((cell) => toCellText(cell) === "");

/**
 * Headers come from user-authored files, so they can be blank, duplicated or
 * named something that would poison the row objects. Each one is made unique
 * and safe to use as an object key.
 */
const normaliseHeaders = (cells: unknown[]): string[] => {
  const usedCounts = new Map<string, number>();

  return cells.map((cell, index) => {
    const fallback = `Column ${index + 1}`;
    // Excel writes a BOM ahead of the first header when saving CSV as UTF-8.
    const text = toCellText(cell).replace(/^﻿/, "");
    const base = !text || UNSAFE_HEADERS.indexOf(text) !== -1 ? fallback : text;

    const seen = usedCounts.get(base) ?? 0;
    usedCounts.set(base, seen + 1);
    return seen === 0 ? base : `${base} (${seen + 1})`;
  });
};

/**
 * Drops trailing columns that hold nothing at all — no header and no value in
 * any row. Spreadsheet exports often carry a few, and each one would otherwise
 * become a "Column N" row in the mapping step for a column the file never used.
 *
 * Only trailing columns go: a blank header with data under it is kept, since
 * the user can still map it.
 */
const trimEmptyTrailingColumns = (
  headerCells: unknown[],
  dataRows: unknown[][]
): unknown[] => {
  let width = headerCells.length;

  while (width > 0) {
    const index = width - 1;
    const isUsed =
      toCellText(headerCells[index]) !== "" ||
      dataRows.some((cells) => toCellText(cells[index]) !== "");

    if (isUsed) break;
    width -= 1;
  }

  return headerCells.slice(0, width);
};

/**
 * Header names the file uses more than once, in the order they first appear.
 *
 * Case and surrounding spaces are ignored, since "Email" and "email " name the
 * same column to anyone reading the sheet. Blank headers are not duplicates of
 * each other — each becomes a column of its own.
 */
const findDuplicateHeaders = (cells: unknown[]): string[] => {
  const seen = new Map<string, string>();
  const duplicates = new Map<string, string>();

  cells.forEach((cell) => {
    const text = toCellText(cell).replace(/^﻿/, "");
    if (!text) return;

    const key = text.toLowerCase();
    const first = seen.get(key);

    if (first === undefined) {
      seen.set(key, text);
      return;
    }

    duplicates.set(key, first);
  });

  return Array.from(duplicates.values());
};

/** Turns a header row + data rows into objects keyed by column header. */
const toRawRows = (
  headers: string[],
  dataRows: unknown[][]
): ContactImportRawRow[] =>
  dataRows.map((cells) => {
    const row: ContactImportRawRow = {};
    headers.forEach((header, index) => {
      row[header] = toCellText(cells[index]);
    });
    return row;
  });

/** Parsers are loaded on demand so they stay out of the main CRM bundle. */
const readCsv = async (file: File): Promise<unknown[][]> => {
  const { parse } = await import("papaparse");

  return new Promise<unknown[][]>((resolve, reject) => {
    parse<string[], File>(file, {
      skipEmptyLines: "greedy",
      complete: (results) => resolve(results.data),
      error: (error: Error) =>
        reject(
          new ContactImportError(
            error.message || "This file could not be read."
          )
        ),
    });
  });
};

const readXlsx = async (file: File): Promise<unknown[][]> => {
  // `read-excel-file/browser` reads the workbook inside a web worker.
  const { readSheet } = await import("read-excel-file/browser");
  return (await readSheet(file)) as unknown[][];
};

/**
 * Reads the selected file and returns its rows keyed by column header.
 *
 * @throws {ContactImportError} for anything the user can act on — an
 * unsupported extension, an oversized file, an unreadable file, or a file
 * with no rows.
 */
export const parseContactFile = async (
  file: File
): Promise<ParsedContactFile> => {
  const format = getContactImportFormat(file.name);

  if (!format) {
    throw new ContactImportError(
      "Unsupported file format. Upload a CSV or XLSX file."
    );
  }

  if (file.size > CONTACT_IMPORT_MAX_FILE_SIZE_BYTES) {
    throw new ContactImportError(
      `File must be under ${CONTACT_IMPORT_MAX_FILE_SIZE_MB}MB. Your file is ${toMegabytes(
        file.size
      )}MB.`
    );
  }

  let matrix: unknown[][];
  try {
    matrix = format.label === "CSV" ? await readCsv(file) : await readXlsx(file);
  } catch (error) {
    if (error instanceof ContactImportError) throw error;
    throw new ContactImportError(
      "This file could not be read. It may be corrupted or password protected."
    );
  }

  const [headerCells, ...bodyCells] = matrix;

  if (!headerCells || isBlankRow(headerCells)) {
    throw new ContactImportError(
      "This file is empty. Add a header row and at least one contact."
    );
  }

  const populatedRows = bodyCells.filter((cells) => !isBlankRow(cells));

  if (populatedRows.length === 0) {
    throw new ContactImportError("No contacts were found in this file.");
  }

  const usedHeaderCells = trimEmptyTrailingColumns(headerCells, populatedRows);
  const headers = normaliseHeaders(usedHeaderCells);
  const duplicateHeaders = findDuplicateHeaders(usedHeaderCells);

  const truncated = populatedRows.length > CONTACT_IMPORT_MAX_ROWS;
  const includedRows = truncated
    ? populatedRows.slice(0, CONTACT_IMPORT_MAX_ROWS)
    : populatedRows;

  return {
    fileName: file.name,
    format: format.label,
    headers,
    rows: toRawRows(headers, includedRows),
    duplicateHeaders,
    rowCount: includedRows.length,
    columnCount: headers.length,
    totalRowsInFile: populatedRows.length,
    truncated,
  };
};

/**
 * Checks a file that read cleanly for content the import cannot work with.
 *
 * This is the file-level gate only — it says whether the sheet is shaped like a
 * contact import at all. Whether the individual contacts are valid is settled
 * later, in the Validate step.
 *
 * @returns the reason the file cannot proceed, or null when it can.
 */
export const getContactFileIncompatibility = (
  parsedFile: ParsedContactFile,
  analysis?: Pick<ContactImportHeaderAnalysis, "missingFields">
): ContactImportIncompatibility | null => {
  // None of the columns carries a field the import cannot do without. The
  // column's name need not match the field's — any of the headers the field is
  // known by counts — so what is named here is the field itself.
  // Checked first: while two columns share a name there is no telling which one
  // a field would be read from, so nothing else about the file matters yet.
  const duplicates = parsedFile.duplicateHeaders ?? [];

  if (duplicates.length > 0) {
    const isOne = duplicates.length === 1;

    return {
      description: "This file has duplicate columns — fix them and upload again.",
      title: "File is not compatible with Contact Import",
      message: `${duplicates.map((header) => `"${header}"`).join(", ")} ${
        isOne ? "appears" : "appear"
      } more than once in your file. Contact import needs one column per field, so rename or remove the ${
        isOne ? "duplicate" : "duplicates"
      } and upload again.`,
      highlight: "columns",
    };
  }

  const missingFields = analysis?.missingFields ?? [];

  if (missingFields.length > 0) {
    const fieldNames = missingFields.map((field) => field.label).join(", ");
    const isOne = missingFields.length === 1;

    return {
      description: "This file is missing required fields — add them and upload again.",
      title: "File is not compatible with Contact Import",
      message: `No ${isOne ? "column for" : "columns for"} ${fieldNames} could be found in your file. Contact import needs one column per required field. Add the missing ${isOne ? "column" : "columns"
        } and upload again.`,
      highlight: "columns",
    };
  }

  // The import takes a fixed number of rows at a time, so an oversized file is
  // stopped here rather than part-imported.
  if (parsedFile.truncated) {
    return {
      description: "This file has too many rows — split it and upload again.",
      title: "File is not compatible with Contact Import",
      message: `Your file has ${parsedFile.totalRowsInFile.toLocaleString()} rows. Contact import takes up to ${CONTACT_IMPORT_MAX_ROWS.toLocaleString()} rows at a time. Split it into smaller files and upload again.`,
      highlight: "rows",
    };
  }

  return null;
};

/**
 * Checks a file against the ceilings the upload call reported.
 *
 * The limits enforced before the upload are this app's own constants, which
 * only match the server's until someone changes one. What the API just said is
 * authoritative, so it gets the last word.
 *
 * @returns why the file is over a server limit, or null when it is within them.
 */
export const getUploadLimitBreach = (
  parsedFile: ParsedContactFile,
  upload: Pick<ContactImportUpload, "maxRows" | "maxSizeBytes" | "fileSizeBytes">
): ContactImportIncompatibility | null => {
  if (upload.maxRows > 0 && parsedFile.totalRowsInFile > upload.maxRows) {
    return {
      description: "This file has too many rows — split it and upload again.",
      title: "File is not compatible with Contact Import",
      message: `Your file has ${parsedFile.totalRowsInFile.toLocaleString()} rows. Contact import takes up to ${upload.maxRows.toLocaleString()} rows at a time. Split it into smaller files and upload again.`,
      highlight: "rows",
    };
  }

  if (upload.maxSizeBytes > 0 && upload.fileSizeBytes > upload.maxSizeBytes) {
    const asMb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)}MB`;

    return {
      description: "This file is too large — split it and upload again.",
      title: "File is not compatible with Contact Import",
      message: `Your file is ${asMb(upload.fileSizeBytes)}. Contact import takes files up to ${asMb(
        upload.maxSizeBytes
      )}. Split it into smaller files and upload again.`,
      highlight: "rows",
    };
  }

  return null;
};

/**
 * Matching the file's own column headers to contact fields.
 *
 * A first pass at the mapping only — every row stays editable in the Map Fields
 * step, and the status says how confident the guess was.
 */

/** Header text reduced to letters and digits, so "Email Address" ≈ "email_address". */
const normaliseFieldName = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Normalised headers that name a field the field's own wording would miss.
 *
 * Keys are already normalised, so one entry covers every spelling that reduces
 * to it — "E-mail", "e mail" and "EMail" all arrive here as `email`.
 */
const CONTACT_FIELD_ALIASES: Record<string, ContactImportFieldKey> = {
  fname: "CONTACT_FIRST_NAME",
  givenname: "CONTACT_FIRST_NAME",
  // A file with a single "Name" column is naming the contact, not their surname.
  name: "CONTACT_FIRST_NAME",
  contactname: "CONTACT_FIRST_NAME",
  lname: "CONTACT_LAST_NAME",
  surname: "CONTACT_LAST_NAME",
  familyname: "CONTACT_LAST_NAME",
  emailaddress: "CONTACT_EMAIL",
  emailid: "CONTACT_EMAIL",
  mail: "CONTACT_EMAIL",
  workemail: "CONTACT_EMAIL",
  phonenumber: "CONTACT_PHONE",
  telephone: "CONTACT_PHONE",
  telephonenumber: "CONTACT_PHONE",
  tel: "CONTACT_PHONE",
  mobile: "CONTACT_PHONE",
  mobilenumber: "CONTACT_PHONE",
  contactnumber: "CONTACT_PHONE",
  designation: "CONTACT_JOB_TITLE",
  title: "CONTACT_JOB_TITLE",
  role: "CONTACT_JOB_TITLE",
  companyname: "CONTACT_COMPANY_NAME",
  organisation: "CONTACT_COMPANY_NAME",
  organization: "CONTACT_COMPANY_NAME",
  account: "CONTACT_COMPANY_NAME",
  // "Website" on a contact file is the company's, not the contact's — there is
  // no other site a contact row carries. Bare "url" is left out: on these files
  // it is as often the LinkedIn profile as the company site.
  website: "CONTACT_COMPANY_WEBSITE_URL",
  websiteurl: "CONTACT_COMPANY_WEBSITE_URL",
  companyurl: "CONTACT_COMPANY_WEBSITE_URL",
  companysite: "CONTACT_COMPANY_WEBSITE_URL",
  companydomain: "CONTACT_COMPANY_WEBSITE_URL",
  weburl: "CONTACT_COMPANY_WEBSITE_URL",
  domain: "CONTACT_COMPANY_WEBSITE_URL",
  linkedinurl: "CONTACT_LINKEDIN_URL",
  linkedinprofile: "CONTACT_LINKEDIN_URL",
  leadstage: "CONTACT_LIFECYCLE_STAGE",
  stage: "CONTACT_LIFECYCLE_STAGE",
  lifecycle: "CONTACT_LIFECYCLE_STAGE",
  customerstage: "CONTACT_LIFECYCLE_STAGE",
  engagement: "CONTACT_ENGAGEMENT_STATUS",
  status: "CONTACT_ENGAGEMENT_STATUS",
  engagementstage: "CONTACT_ENGAGEMENT_STATUS",
  owneremail: "CONTACT_OWNER_EMAIL",
  ownername: "CONTACT_OWNER_EMAIL",
  salesowner: "CONTACT_OWNER_EMAIL",
  accountowner: "CONTACT_OWNER_EMAIL",
  contactowner: "CONTACT_OWNER_EMAIL",
  assignedto: "CONTACT_OWNER_EMAIL",
  tag: "CONTACT_TAGS",
  labels: "CONTACT_TAGS",
  note: "CONTACT_INSIGHTS",
  notes: "CONTACT_INSIGHTS",
  remarks: "CONTACT_INSIGHTS",
  comments: "CONTACT_INSIGHTS",
};

/** An exact hit on the field's own label or key, otherwise a known alias. */
const matchContactField = (
  header: string
): { field: ContactImportFieldKey; status: "AUTO" | "SUGGESTED" } | null => {
  const normalised = normaliseFieldName(header);
  if (!normalised) return null;

  const exact = CONTACT_IMPORT_TARGET_FIELDS.find(
    (target) =>
      normaliseFieldName(target.label) === normalised ||
      normaliseFieldName(target.value) === normalised
  );
  if (exact) return { field: exact.value, status: "AUTO" };

  const alias = CONTACT_FIELD_ALIASES[normalised];
  return alias ? { field: alias, status: "SUGGESTED" } : null;
};

/**
 * Builds one mapping row per column found in the uploaded file.
 *
 * The file's own headers are carried through untouched — they are what the Map
 * Fields step lists, and what the rows are keyed by. Only the field each one is
 * matched to is inferred.
 *
 * A contact field is claimed once. Exact matches claim theirs before aliases
 * do, so a file carrying both "Name" and "First Name" gives First Name to the
 * column that names it outright, whichever order the file lists them in. The
 * column left over is marked ambiguous rather than guessed at.
 */
export const buildContactFieldMappings = (
  headers: string[]
): ContactImportColumnMapping[] => {
  const matches = headers.map((header) => matchContactField(header));
  const claimedFields = new Set<ContactImportFieldKey>();

  // Nothing is skipped on the user's behalf: a column we could not place is
  // left without an answer, for them to give one.
  const mappings: ContactImportColumnMapping[] = headers.map((header) => ({
    fileColumn: header,
    field: CONTACT_IMPORT_UNSET_FIELD,
    status: "UNMAPPED",
  }));

  (["AUTO", "SUGGESTED"] as const).forEach((tier) => {
    matches.forEach((match, index) => {
      if (!match || match.status !== tier || claimedFields.has(match.field)) return;

      claimedFields.add(match.field);
      mappings[index] = {
        fileColumn: headers[index],
        field: match.field,
        status: tier,
      };
    });
  });

  // A column that matched a field another column already feeds is ambiguous,
  // not unrecognised: the user settles it in Map Fields.
  matches.forEach((match, index) => {
    if (match && mappings[index].field === CONTACT_IMPORT_UNSET_FIELD) {
      mappings[index] = { ...mappings[index], status: "AMBIGUOUS" };
    }
  });

  return mappings;
};

/** A field's own label, preferring the one the API gave it. */
const getFieldLabel = (
  field: ContactImportFieldKey,
  apiLabel?: string | null
): string =>
  apiLabel ??
  CONTACT_IMPORT_TARGET_FIELDS.find((target) => target.value === field)?.label ??
  field;

/**
 * Builds the Map Fields rows from the columns the import API read.
 *
 * The API's headers are used verbatim — they are the keys a saved mapping is
 * sent back under, so nothing may reword them.
 *
 * Which field each column feeds is settled in order: the API's own match wins;
 * where it has none, a header this app recognises is offered as a suggestion;
 * otherwise the column is left for the user. A field is never claimed twice, so
 * a suggestion stands down for a column the API already mapped.
 */
export const buildMappingsFromImportColumns = (
  columns: ContactImportColumnInfo[]
): ContactImportColumnMapping[] => {
  // The API maps onto its own catalogue, which is wider than what this screen
  // offers. A field the dropdown cannot show is not one the user could ever
  // change or agree to, so the column is handed back to them unanswered — and
  // the save then sends `null` for it, taking the field off the column rather
  // than leaving the API quietly feeding one nobody asked for.
  const isOfferedField = (
    field: ContactImportFieldKey | null
  ): field is ContactImportFieldKey =>
    Boolean(field) &&
    CONTACT_IMPORT_TARGET_FIELDS.some((target) => target.value === field);

  const claimedFields = new Set<ContactImportFieldKey>(
    columns.map((column) => column.mappedField).filter(isOfferedField)
  );

  return columns.map((column) => {
    if (isOfferedField(column.mappedField)) {
      // A header that already reads as the field's own name needed no guessing.
      const isExact =
        normaliseFieldName(column.header) ===
        normaliseFieldName(getFieldLabel(column.mappedField, column.mappedFieldLabel));

      return {
        fileColumn: column.header,
        field: column.mappedField,
        status: isExact ? "AUTO" : "SUGGESTED",
      };
    }

    const guess = matchContactField(column.header);

    if (guess && !claimedFields.has(guess.field)) {
      claimedFields.add(guess.field);
      return { fileColumn: column.header, field: guess.field, status: "SUGGESTED" };
    }

    return {
      fileColumn: column.header,
      // Left for the user to answer rather than skipped for them — see
      // buildContactFieldMappings.
      field: CONTACT_IMPORT_UNSET_FIELD,
      // A guess the API already spent on another column is ambiguous, not
      // unrecognised — the user settles which column keeps the field.
      status: guess ? "AMBIGUOUS" : "UNMAPPED",
    };
  });
};

/**
 * Reads the uploaded file's headers and says whether the import can go ahead.
 *
 * Required fields are settled by which CRM field each column was matched to,
 * never by the header's literal text — "Designation" satisfies Job Title just
 * as "Job Title" does. Columns that match nothing are simply left unmapped.
 *
 * The mappings come back with the answer so the Map Fields step opens with the
 * detections already applied rather than repeating the work.
 */
export const analyseContactImportHeaders = (
  headers: string[]
): ContactImportHeaderAnalysis => {
  const mappings = buildContactFieldMappings(headers);
  const missingFields = getUnmappedRequiredFields(mappings);

  return { isValid: missingFields.length === 0, missingFields, mappings };
};

/**
 * The mapping as the API takes it — every column the screen showed, including
 * the ones left out.
 *
 * A skipped column is sent as `null` rather than omitted: the API merges what
 * it is given, so leaving one out would quietly keep the field it used to feed.
 */
export const toContactFieldMap = (
  mappings: ContactImportColumnMapping[]
): ContactImportFieldMap =>
  mappings.reduce<ContactImportFieldMap>((fieldMap, mapping) => {
    const { field } = mapping;

    // A column with no field goes as `null` whether the user skipped it or
    // never answered for it: either way it feeds nothing, and saying so is
    // what stops the API keeping a field it fed before.
    fieldMap[mapping.fileColumn] =
      field === CONTACT_IMPORT_NO_FIELD || field === CONTACT_IMPORT_UNSET_FIELD
        ? null
        : field;
    return fieldMap;
  }, {});

/**
 * Columns still waiting on the user — neither given a field nor skipped.
 *
 * A skip is an answer and settles the column; these have none, and the step
 * holds until they do. Nothing is decided on the user's behalf, so the import
 * never quietly drops a column they meant to map.
 */
export const getUndecidedColumns = (
  mappings: ContactImportColumnMapping[]
): ContactImportColumnMapping[] =>
  mappings.filter((mapping) => mapping.field === CONTACT_IMPORT_UNSET_FIELD);

/** Required fields that no column feeds yet, so the import cannot run. */
export const getUnmappedRequiredFields = (
  mappings: ContactImportColumnMapping[]
): ContactImportTargetField[] =>
  CONTACT_IMPORT_REQUIRED_FIELDS.filter(
    (required) => !mappings.some((mapping) => mapping.field === required.value)
  );

// The import template is no longer built here: it comes from the API, which
// generates it from the same field catalog the import itself validates against.

/**
 * Owner options, named so two people cannot be confused for one another.
 *
 * A CRM can hold two Sam Lees, and the import's whole reason for asking is that
 * it refuses to guess between them — so a shared name carries its email.
 */
export const toOwnerDropdownOptions = (
  owners: ContactOwner[]
): ContactImportDropdownOption[] => {
  const nameCounts = new Map<string, number>();
  owners.forEach((owner) => {
    nameCounts.set(owner.name, (nameCounts.get(owner.name) ?? 0) + 1);
  });

  return owners.map((owner) => ({
    value: owner.uuid,
    label:
      (nameCounts.get(owner.name) ?? 0) > 1 && owner.email
        ? `${owner.name} · ${owner.email}`
        : owner.name,
  }));
};

/**
 * Adds the records the import already matched to the picker's options.
 *
 * A match names a record the list may not carry — it can be paged, filtered, or
 * simply not have arrived — and a row whose option is missing would read as
 * unmatched when it is not. What the import matched is always offered.
 */
/**
 * Whether any of a card's lists carries on past the page that arrived.
 *
 * The API sends a `total*Pages` beside every `total*Count`, worked out from the
 * size the call asked for. Only page 0 is ever read, so more than one page of
 * anything means there is more to fetch. Nothing said means nothing known —
 * the caller falls back to counting what it was given.
 */
const hasFurtherPages = (
  ...pageCounts: (number | undefined | null)[]
): boolean | undefined => {
  const stated = pageCounts.filter(
    (count): count is number => typeof count === "number"
  );

  return stated.length > 0 ? stated.some((count) => count > 1) : undefined;
};

/**
 * The uploaded value behind a do-not-import entry.
 *
 * It used to be the entry itself; it is now an object carrying the value's own
 * `occurrences` too. Both shapes are read, so the screen does not depend on
 * which side of that change the API it is talking to sits on.
 */
const toDoNotImportValue = (
  entry: ContactImportDoNotImportValue
): string => (typeof entry === "string" ? entry : entry.uploadedValue);

/**
 * Rows for the values the import already holds as excluded.
 *
 * The row carries the local "None" target, which is what the step reads an
 * exclusion from either way: a choice made on an earlier visit and one made a
 * moment ago look the same, and both stay a picker the user can change. The
 * count comes with the value now, so a row here stands for as many rows of the
 * file as any other.
 */
const toDoNotImportRows = (
  entries: ContactImportDoNotImportValue[] = []
): ContactImportDropdownValue[] =>
  entries.map((entry) => ({
    fileValue: toDoNotImportValue(entry),
    targetValue: CONTACT_IMPORT_NO_VALUE,
    notFound: false,
    ...(typeof entry === "string" || entry.occurrences == null
      ? {}
      : { occurrences: entry.occurrences }),
  }));

const withMatchedOptions = (
  options: ContactImportDropdownOption[],
  matches: { value: string; label: string }[]
): ContactImportDropdownOption[] => {
  const known = new Set(options.map((option) => option.value));

  return [
    ...options,
    ...matches.filter(
      (match, index) =>
        match.value &&
        !known.has(match.value) &&
        matches.findIndex((other) => other.value === match.value) === index
    ),
  ];
};

/**
 * Builds the Dropdowns step's Owner card from what the import knows.
 *
 * Both of the API's lists become rows: an exact email match is settled, a
 * name-only match is a suggestion the user confirms, and either can be pointed
 * somewhere else. Values that matched nothing are never listed one by one —
 * they are counted, and answered by the single decision under the rows.
 */
/**
 * What the Owner card says above its Assign / Skip choices.
 *
 * Takes the count rather than the field so the card can re-compose it from the
 * number it is showing at that moment: setting a value to None adds records to
 * the pool and picking a user for one takes them out, neither of which the API
 * has been told about yet.
 */
export const getOwnerDecisionMessage = (unresolvedRecords: number): string =>
  `${unresolvedRecords.toLocaleString()} ${
    unresolvedRecords === 1 ? "record still needs" : "records still need"
  } an Owner. These values are either blank or don't match an existing Worklap user. Select the correct user from the dropdown, or use the options below to resolve all affected records.`;

export const buildOwnerDropdownField = (
  ownerMapping: ContactImportOwnerMapping,
  owners: ContactOwner[] = []
): ContactImportDropdownField => {
  const matched = ownerMapping.matched ?? [];
  const suggestions = ownerMapping.suggestions ?? [];
  const unresolvedOwners = ownerMapping.unresolvedOwners ?? [];

  const values: ContactImportDropdownValue[] = [];
  const seenFileValues = new Set<string>();

  // Matched records from API (exact matches)
  matched.forEach((match) => {
    const key = match.uploadedValue.trim().toLowerCase();
    if (!seenFileValues.has(key)) {
      seenFileValues.add(key);
      values.push({
        fileValue: match.uploadedValue,
        targetValue: match.matchedUserUuid,
        occurrences: match.occurrences,
        notFound: false,
        confidence: "EXACT",
      });
    }
  });

  // A suggestion is a guess the API will not act on by itself — a name with no
  // email behind it, or one close enough to be a typo. It is pre-filled like a
  // match but marked apart, since the row stays blocked until it is agreed to.
  suggestions.forEach((suggestion) => {
    const key = suggestion.uploadedValue?.trim().toLowerCase();
    if (key && !seenFileValues.has(key)) {
      seenFileValues.add(key);
      values.push({
        fileValue: suggestion.uploadedValue,
        targetValue: suggestion.matchedUserUuid,
        occurrences: suggestion.occurrences,
        notFound: false,
        confidence: suggestion.fuzzyMatch ? "FUZZY" : "SUGGESTED",
      });
    }
  });

  // Values already marked do-not-import keep a row, so the choice is visible
  // and reversible rather than silently dropped from the card. Excluding a
  // value takes it out of the other lists, so one showing up in both is the API
  // contradicting itself — the row it already has stands, and rows stay unique.
  const doNotImport = (ownerMapping.doNotImportOwners ?? []).filter((entry) => {
    const key = toDoNotImportValue(entry)?.trim().toLowerCase();
    if (!key || seenFileValues.has(key)) return false;
    seenFileValues.add(key);
    return true;
  });
  values.push(...toDoNotImportRows(doNotImport));

  // Values the import could not resolve are deliberately not listed: no user
  // can be picked for them, so a row would offer a choice that does not exist.
  // They are counted instead, and answered by the decision under the rows.
  const totalUnresolved =
    ownerMapping.totalUnresolvedCount ?? unresolvedOwners.length;
  const unresolvedCount =
    ownerMapping.affectedRecordCount ?? totalUnresolved;

  return {
    key: "CONTACT_OWNER_EMAIL",
    label: "Owner",
    kind: "REQUIRED",
    uniqueValueCount:
      // Counts what the card lists; the rest is spoken for by the decision.
      (ownerMapping.totalMatchedCount ?? matched.length) +
      (ownerMapping.totalSuggestionCount ?? suggestions.length) +
      (ownerMapping.totalDoNotImportCount ?? doNotImport.length),
    hasMorePages: hasFurtherPages(
      ownerMapping.totalMatchedPages,
      ownerMapping.totalSuggestionPages,
      ownerMapping.totalDoNotImportPages
    ),
    unresolvedCount,
    placeholder: "No value",
    options: withMatchedOptions(
      toOwnerDropdownOptions(owners),
      [...matched, ...suggestions].map((match) => ({
        value: match.matchedUserUuid,
        label: match.matchedUserName,
      }))
    ),
    values,
    // Carried whether or not anything needs it right now; whether it is on
    // screen is decided at render, from the count as it stands then. A value
    // marked do-not-import is settled on its own and never reaches this pool.
    decision: {
      tone: "warning",
      // Seeded from what the API last said; the card re-composes it from the
      // count it is showing, so a pick made since does not leave a stale
      // number on screen.
      message: getOwnerDecisionMessage(unresolvedCount),
      actions: [
        { value: "ASSIGN_ME", label: "Assign me as Owner" },
        { value: "SKIP", label: "Skip these records" },
      ],
    },
    // A decision already sent comes back on the import, so reopening the step
    // shows what was chosen rather than asking again.
    selectedChoice: ownerMapping.resolution?.action,
    isFromApi: true,
  };
};

/**
 * Builds the Dropdowns step's Company card from what the import knows.
 *
 * Matches and suggestions become rows, and so do the unresolved values the API
 * sampled — a name Worklap does not hold can still be pointed at a company by
 * hand. What is left over is answered by the one decision under the rows, which
 * links the whole set to an existing company or leaves it out: an import never
 * creates a company.
 */
/**
 * What the Company card says above its Link / Skip choices.
 *
 * The count is worked out from the card as it stands, not from what the API
 * last reported: picking a company for a row settles those contacts on the
 * spot. Contacts whose Company cell is blank stay in it either way — they
 * carry no value, so no row on the card reaches them.
 */
export const getCompanyDecisionMessage = (
  field: ContactImportDropdownField
): string => {
  const total = getUnresolvedRecordCount(field);

  return `${total.toLocaleString()} ${
    total === 1 ? "contact has" : "contacts have"
  } a Company that couldn't be matched or is missing. Choose how you'd like to handle all of them.`;
};

/**
 * The Company card's two choices, each naming how many contacts it would act
 * on right now.
 *
 * Built beside the message and from the same count, so the choice cannot say
 * one number while the line above it says another.
 */
export const getCompanyDecisionChoices = (
  field: ContactImportDropdownField
): ContactImportDropdownChoice[] => {
  const total = getUnresolvedRecordCount(field);
  const isOne = total === 1;
  const count = total.toLocaleString();

  return [
    {
      value: "LINK_TO_EXISTING",
      title: "Link to an existing Company",
      description: `${
        isOne ? "Assign this contact" : `Assign all ${count} contacts`
      } to one existing Company you select. No new Company is created.`,
    },
    {
      value: "SKIP",
      title: "Skip these contacts",
      description: `${
        isOne ? "This contact won't" : `These ${count} contacts won't`
      } be imported now. You can add them later once a matching Company exists in Worklap.`,
    },
  ];
};

export const buildCompanyDropdownField = (
  companyMapping: ContactImportCompanyMapping,
  companies: Company[]
): ContactImportDropdownField => {
  // Same here: a list the API left out means none of that kind.
  const matched = companyMapping.matched ?? [];
  const suggestions = companyMapping.suggestions ?? [];
  const unresolved = companyMapping.unresolvedCompany ?? companyMapping.unresolvedSample ?? [];

  const toMatchedRow = (
    match: ContactImportCompanyMatch
  ): ContactImportDropdownValue => ({
    fileValue: match.uploadedValue,
    targetValue: match.matchedCompanyUuid,
    occurrences: match.occurrences,
    notFound: false,
    confidence: "EXACT",
  });

  // A suggestion arrives answered, exactly as Owner's does: the row opens on
  // the company the API proposed — or on the one the user already confirmed,
  // which outranks it — and stays a picker they can change. It is badged as a
  // guess rather than left blank, since a blank would read as "nothing here"
  // and hold the step over a value the API has already spoken for.
  const toSuggestedRow = (
    suggestion: ContactImportCompanySuggestion
  ): ContactImportDropdownValue => ({
    fileValue: suggestion.uploadedValue,
    targetValue:
      suggestion.confirmedCompanyUuid || suggestion.suggestedCompanyUuid,
    occurrences: suggestion.occurrences,
    notFound: false,
    confidence: suggestion.fuzzyMatch ? "FUZZY" : "SUGGESTED",
  });

  // A value the API named in both lists is answered by the row it already has;
  // listing it again as unresolved would hold the step over a settled value.
  const answered = new Set([
    ...matched.map((match) => match.uploadedValue),
    ...suggestions.map((suggestion) => suggestion.uploadedValue),
  ]);

  const listed: ContactImportDropdownValue[] = [
    ...matched.map(toMatchedRow),
    ...suggestions.map(toSuggestedRow),
    ...unresolved
      .filter((uploadedValue) => !answered.has(uploadedValue))
      .map((uploadedValue) => ({
        fileValue: uploadedValue,
        targetValue: "",
        notFound: true,
      })),
  ];

  // An excluded value leaves the other lists, so one in both is the API
  // contradicting itself — the row it already has stands, and rows stay unique.
  const alreadyListed = new Set(listed.map((value) => value.fileValue));
  const doNotImport = (companyMapping.doNotImportCompany ?? []).filter((entry) => {
    const uploadedValue = toDoNotImportValue(entry);
    return Boolean(uploadedValue) && !alreadyListed.has(uploadedValue);
  });

  const values: ContactImportDropdownValue[] = [
    ...listed,
    ...toDoNotImportRows(doNotImport),
  ];

  // The company a row opens on has to be in its own options or the picker has
  // nothing to show for it — and a suggested one may not be on the page of
  // companies the picker fetched.
  const options = withMatchedOptions(
    companies.map((company) => ({ value: company.uuid, label: company.name })),
    [
      ...matched.map((match) => ({
        value: match.matchedCompanyUuid,
        label: match.matchedCompanyName,
      })),
      ...suggestions.map((suggestion) => ({
        value: suggestion.suggestedCompanyUuid,
        label: suggestion.suggestedCompanyName,
      })),
    ]
  );

  const totalMatched = companyMapping.totalMatchedCount ?? matched.length;
  const totalSuggested = companyMapping.totalSuggestionCount ?? suggestions.length;
  const totalUnresolved = companyMapping.totalUnresolvedCount ?? unresolved.length;
  const hiddenUnresolved = totalUnresolved - unresolved.length;

  // The API's own affected/not-found figures are not read here any more: the
  // card works its count out from the rows as they stand, and those two are
  // frozen at whatever the last save reported.
  const missingContactCount = companyMapping.missingContactCount ?? 0;

  return {
    key: "CONTACT_COMPANY_NAME",
    label: "Company",
    kind: "REQUIRED",
    uniqueValueCount:
      totalMatched +
      totalSuggested +
      totalUnresolved +
      (companyMapping.totalDoNotImportCount ?? doNotImport.length),
    // Contacts whose Company cell is blank carry no uploaded value, so no row
    // on this card can ever reach them — only the decision under the rows can.
    // Counting them holds the step until it is taken, which is also what the
    // API asks for: it refuses this block's save while anything is unresolved
    // and no fallback has been chosen.
    //
    // The unresolved values still on later pages go in here for the same
    // reason: they have no row either, so counting only the rows on screen
    // would report a file of 500 as 150 — the page size, not the file.
    unresolvedCount:
      missingContactCount + (hiddenUnresolved > 0 ? hiddenUnresolved : 0),
    hasMorePages: hasFurtherPages(
      companyMapping.totalMatchedPages,
      companyMapping.totalSuggestionPages,
      companyMapping.totalUnresolvedPages,
      companyMapping.totalDoNotImportPages
    ),
    placeholder: "No match in Worklap",
    options,
    values,
    moreNotFoundCount: hiddenUnresolved > 0 ? hiddenUnresolved : undefined,
    alert:
      missingContactCount > 0
        ? `${missingContactCount.toLocaleString()} ${missingContactCount === 1 ? "contact has" : "contacts have"
        } no Company value in the file — mapping isn't possible for ${missingContactCount === 1 ? "it" : "these"
        }.`
        : undefined,
    // Carried whether or not anything needs it right now, the same as Owner's:
    // a file that arrived fully matched still needs these the moment a value
    // is set to None, and attaching it only when the API already counted
    // something left no way back. Whether it is on screen is decided at
    // render, from the count as it stands then.
    //
    // The message and the choices below are seeds. The card rebuilds both from
    // the live count — see getCompanyDecisionMessage / getCompanyDecisionChoices
    // — so the wording lives there and cannot drift between the two.
    decision: {
      tone: "info",
      message: "Choose how to handle unresolved Company records.",
      choices: [
        {
          value: "LINK_TO_EXISTING",
          title: "Link to an existing Company",
          description:
            "These contacts are linked to one existing Company you select. No new Company is created.",
        },
        {
          value: "SKIP",
          title: "Skip these contacts",
          description:
            "These contacts won't be imported now. You can add them later once a matching Company exists in Worklap.",
        },
      ],
      selection: {
        forChoice: "LINK_TO_EXISTING",
        label: "Company to link them to",
        placeholder: "Choose a company",
        options,
        value: companyMapping.resolution?.companyUuid,
      },
    },
    selectedChoice: companyMapping.resolution?.action,
    isFromApi: true,
  };
};

/**
 * The value mappings one screen of the Dropdowns step has to save.
 *
 * Each field it asked about contributes its block in full, since a saved block
 * replaces what was there. Cards standing in for data the API has not sent yet
 * are left out, as are values still waiting on a choice.
 */
export const toValueMappingBlocks = (
  fields: ContactImportDropdownField[]
): Partial<Record<ContactImportFieldKey, Record<string, string>>> => {
  const blocks: Partial<Record<ContactImportFieldKey, Record<string, string>>> = {};

  fields.forEach((field) => {
    if (!field.isFromApi) return;

    const block: Record<string, string> = {};
    field.values.forEach((value) => {
      if (!value.targetValue) return;

      if (value.targetValue === CONTACT_IMPORT_NO_VALUE) {
        // "NONE" leaves every row carrying this value out of the import and
        // takes its records out of the fallback's count (APISPEC §8.5). Sent
        // for every block, enum columns included — §10 has the API refusing it
        // there, so that is the one thing to check if Lifecycle Stage or
        // Engagement Status starts coming back 400.
        block[value.fileValue] = CONTACT_IMPORT_NONE;
        return;
      }

      block[value.fileValue] = value.targetValue;
    });

    if (Object.keys(block).length > 0) blocks[field.key] = block;
  });

  return blocks;
};

/**
 * Carries the answers already given onto a freshly built set of cards.
 *
 * Asking for a longer page rebuilds every card from the new response, and the
 * picks made since the last save live only on screen — so they are copied
 * across rather than thrown away. What the user chose wins over what the
 * rebuild suggests; a value they never touched takes whatever the import now
 * says about it.
 */
export const withPreservedDropdownAnswers = (
  built: ContactImportDropdownField[],
  previous: ContactImportDropdownField[]
): ContactImportDropdownField[] =>
  built.map((field) => {
    const prior = previous.find((candidate) => candidate.key === field.key);
    if (!prior) return field;

    const answered = new Map(
      prior.values
        .filter((value) => value.targetValue)
        .map((value) => [value.fileValue, value.targetValue])
    );

    // How many rows each value stands for, as the card last knew it.
    //
    // Every list the API sends carries this now, `doNotImportOwners` /
    // `doNotImportCompany` included. This stands behind them for a response
    // that predates that — there a value set aside arrives as a bare name, and
    // rebuilt from it alone would stand for one row whatever the file says,
    // throwing off every count drawn from it.
    const occurrencesByValue = new Map(
      prior.values
        .filter((value) => value.occurrences != null)
        .map((value) => [value.fileValue, value.occurrences as number])
    );

    const priorSelection = prior.decision?.selection?.value;

    // Owner's choice answers for records that have no row of their own. A
    // rebuilt card reporting none of them — a file where every Owner resolved —
    // has nothing for it to answer, so the choice is dropped rather than
    // carried: it would otherwise outlive the file it was made for, show a
    // settled decision the new one never needed, and be sent on Continue.
    // Company is left alone; its count is only the contacts with no Company
    // cell, so nought there does not mean the decision is moot.
    const answersNothing =
      field.key === "CONTACT_OWNER_EMAIL" && (field.unresolvedCount ?? 0) === 0;

    return {
      ...field,
      values: field.values.map((value) => {
        const answer = answered.get(value.fileValue);
        const occurrences = value.occurrences ?? occurrencesByValue.get(value.fileValue);

        if (!answer && occurrences === value.occurrences) return value;

        return {
          ...value,
          ...(answer ? { targetValue: answer } : {}),
          ...(occurrences != null ? { occurrences } : {}),
        };
      }),
      selectedChoice: answersNothing
        ? field.selectedChoice
        : prior.selectedChoice ?? field.selectedChoice,
      decision:
        field.decision && field.decision.selection
          ? {
              ...field.decision,
              selection: {
                ...field.decision.selection,
                value: priorSelection ?? field.decision.selection.value,
              },
            }
          : field.decision,
    };
  });

/**
 * Cards on a screen that still owe an answer.
 *
 * A value the import could not resolve has to be settled before the flow moves
 * on, or its rows fail validation later. Owner and Company accept either
 * answer — map the values one by one, or take the decision that covers the
 * whole set at once. A plain enum field has no such decision, so every one of
 * its values needs a pick.
 */
/**
 * Records a field still needs its decision for.
 *
 * Two lots end up in the same pool. The API's own count covers what it could
 * not match — blanks, unknown emails, names shared by more than one person, a
 * contact whose Company cell was empty. A value the user marked "None" joins
 * them: it is sent with no record behind it, so its rows reach the import
 * unresolved too, and the decision under the rows is the only thing that
 * settles them.
 *
 * Owner and Company only. A plain dropdown has no such decision, so "None"
 * there is an answer in itself — the field is simply left blank.
 */
export const getUnresolvedRecordCount = (
  field: ContactImportDropdownField
): number => {
  const base = field.unresolvedCount ?? 0;
  if (getValueMappingType(field.key) === "DROPDOWN") return base;

  // Rows the decision still speaks for: ones set aside as None, and ones
  // nobody has picked for yet. Picking a record for a row takes it out of the
  // pool there and then, which is what keeps the count on the card honest
  // between saves — the API's own figure is from before any of this.
  //
  // Owner lists no unpicked rows, so only the None arm ever reaches it.
  const answeredByDecision = field.values.reduce(
    (total, value) =>
      !value.targetValue || value.targetValue === CONTACT_IMPORT_NO_VALUE
        ? // A value stands for however many rows carry it; the API does not
          // always count them out, and one is enough to need an answer.
          total + (value.occurrences ?? 1)
        : total,
    0
  );

  return base + answeredByDecision;
};

/**
 * Whether the card's chosen decision is still waiting on the record it has to
 * be pointed at.
 *
 * "Link these to an existing company" names no company until one is picked, so
 * the records it is meant to settle are still open — and the API refuses the
 * call on those same grounds.
 */
export const isDecisionSelectionPending = (
  field: ContactImportDropdownField
): boolean => {
  const selection = field.decision?.selection;

  return Boolean(
    selection && field.selectedChoice === selection.forChoice && !selection.value
  );
};

/**
 * The card on this screen whose Skip would leave the import with nothing.
 *
 * Skip chosen and not one value pointed at a record: there is then nothing for
 * the import to create, so it is stopped rather than run for no result. No
 * counting involved — a single mapped value is enough to mean the file has
 * something in it, and none at all is enough to mean it has not.
 */
/**
 * What the user is told when their Skip would leave the import nothing to do.
 *
 * Each field says it in its own terms — Owner leaves contacts without an
 * owner, Company leaves contacts without a company — so the sentence names
 * what is actually being lost rather than a word that fits both badly.
 */
export const getSkipsEverythingCopy = (
  key: ContactImportFieldKey,
  label: string,
  records: number
): { title: string; body: string; hint?: string } => {
  const isOne = records === 1;
  const count = records.toLocaleString();

  if (key === "CONTACT_COMPANY_NAME") {
    return {
      title: "No companies left to import",
      body: `${
        isOne ? "1 company" : `All ${count} companies`
      } will be skipped. There are no companies left to continue with this import.`,
      hint: "Go back to update the mapping, or close this import.",
    };
  }

  if (key === "CONTACT_OWNER_EMAIL") {
    return {
      title: "No contacts left to import",
      body: `${
        isOne
          ? "1 contact in this file is"
          : `All ${count} contacts in this file are`
      } set to be skipped. Since no contacts remain for import, you can go back and change the owner mapping or close this import.`,
    };
  }

  return {
    title: "Nothing left to import",
    body: `${
      isOne ? "1 record is" : `All ${count} records are`
    } set to be skipped. Go back and change the ${label.toLowerCase()} mapping, or close this import.`,
  };
};

export const getSkipsEverythingField = (
  fields: ContactImportDropdownField[]
): ContactImportDropdownField | undefined =>
  fields.find(
    (field) =>
      field.selectedChoice === "SKIP" &&
      !field.values.some(
        (value) =>
          Boolean(value.targetValue) &&
          value.targetValue !== CONTACT_IMPORT_NO_VALUE
      )
  );

export const getUnresolvedDropdownFields = (
  fields: ContactImportDropdownField[]
): ContactImportDropdownField[] =>
  fields.filter((field) => {
    if (!field.isFromApi) return false;

    if (isDecisionSelectionPending(field)) return true;

    // Only a value still waiting on the user counts against the step; one they
    // deliberately left out is settled.
    const hasUnmappedValue = field.values.some((value) => !value.targetValue);
    // Records left out of the rows still need answering, and only the decision
    // can answer them.
    const hasUnresolvedRecords = getUnresolvedRecordCount(field) > 0;

    if (!hasUnmappedValue && !hasUnresolvedRecords) return false;

    // A decision answers for everything the values left open.
    if (field.decision) return !field.selectedChoice;

    return hasUnmappedValue;
  });

/**
 * What the review found that is worth a word before starting.
 *
 * These are reported, never enforced: whether an import can run is the API's
 * decision, and it is asked rather than second-guessed. Duplicates, invalid
 * rows and missing fields are left out — those are skipped row by row and say
 * nothing about the import as a whole.
 */
export const getReviewWarnings = (
  summary?: ContactImportReviewSummary | null
): string[] => {
  if (!summary) return [];

  const issues: string[] = [];
  const dropdownValues = summary.unmappedDropdownValues ?? [];
  const companyValues = summary.unmappedCompanyValues ?? [];

  if (dropdownValues.length > 0) {
    issues.push(
      `${dropdownValues.length} dropdown ${
        dropdownValues.length === 1 ? "value has" : "values have"
      } no Worklap option yet`
    );
  }

  if (companyValues.length > 0) {
    issues.push(
      `${companyValues.length} company ${
        companyValues.length === 1 ? "value is" : "values are"
      } still unresolved`
    );
  }

  return issues;
};

/**
 * Which block a field's values belong to. Owner and Company each answer for
 * themselves; every other dropdown-backed field is a plain value match.
 */
export const getValueMappingType = (
  field: ContactImportFieldKey
): ContactImportValueMappingType => {
  if (field === "CONTACT_OWNER_EMAIL") return "OWNER";
  if (field === "CONTACT_COMPANY_NAME") return "COMPANY";
  return "DROPDOWN";
};

/**
 * The value-mapping calls a screen has to make, one per block.
 *
 * The API refuses a body mixing blocks, so fields are grouped by the block they
 * answer for and each group is sent on its own. A screen showing only one kind
 * of field — as all three do today — makes exactly one call.
 */
export const toValueMappingRequests = (
  fields: ContactImportDropdownField[]
): ContactImportValueMappingRequest[] => {
  const byType = new Map<
    ContactImportValueMappingType,
    Partial<Record<ContactImportFieldKey, Record<string, string>>>
  >();

  // Every block the screen asked about is answered, even when nothing on it
  // needed a pick: an empty `valueMapping` is the API's way of saying so, and
  // leaving the call out would say nothing at all.
  fields.forEach((field) => {
    if (!field.isFromApi) return;

    const type = getValueMappingType(field.key);
    if (!byType.has(type)) byType.set(type, {});
  });

  Object.entries(toValueMappingBlocks(fields)).forEach(([field, block]) => {
    const fieldKey = field as ContactImportFieldKey;
    const type = getValueMappingType(fieldKey);

    byType.set(type, { ...(byType.get(type) ?? {}), [fieldKey]: block });
  });

  return Array.from(byType, ([type, valueMapping]) => ({ type, valueMapping }));
};

/**
 * Formats an enum or raw string into a clean user-facing label (e.g. "SALES_OPPORTUNITY" -> "Sales Opportunity").
 */
export const formatDropdownOptionLabel = (option: string): string => {
  if (!option) return "";
  if (option.includes("_")) {
    return option
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }
  if (option === option.toUpperCase() && option.length > 1) {
    return option.charAt(0).toUpperCase() + option.slice(1).toLowerCase();
  }
  return option;
};

export const normalizeDropdownOption = (
  opt: string | { value: string; label?: string }
): ContactImportDropdownOption => {
  if (typeof opt === "string") {
    return {
      value: opt,
      label: formatDropdownOptionLabel(opt),
    };
  }
  return {
    value: opt.value,
    label: opt.label || formatDropdownOptionLabel(opt.value),
  };
};

/**
 * Builds a Dropdowns step card for a generic enum field (e.g. Lifecycle Stage, Engagement Status)
 * directly from the dropdownColumns returned by the API (APISPEC Section 8.6).
 *
 * Options come directly from column.options.
 * Uploaded values come directly from column.values.
 */
export const buildGenericDropdownField = (
  column: ContactImportDropdownColumn
): ContactImportDropdownField => {
  const options: ContactImportDropdownOption[] = [
    // Leaving a value out has to be something the user can say, not just the
    // absence of an answer — the step cannot otherwise tell "skip this" from
    // "not done yet".
    { value: CONTACT_IMPORT_NO_VALUE, label: "None" },
    ...(column.options ?? []).map(normalizeDropdownOption),
  ];

  const rawValues = column.values ?? [];
  const values: ContactImportDropdownValue[] = rawValues.map((v) => {
    // Respect mappedValue when already set; prefer existing suggestedValue otherwise
    const saved = v.mappedValue || v.suggestedValue || "";

    // "None" goes to the API under its own name and comes back under it. That
    // name is not one of the column's options, so read as a mapping it would
    // match nothing and the row would fall back to the placeholder — a value
    // the user settled reading as one they never touched. It is the same
    // answer as no match at all, so it is read as one.
    const matched = saved === CONTACT_IMPORT_NONE ? "" : saved;

    return {
      fileValue: v.uploadedValue,
      occurrences: v.occurrences,
      // A contact may be imported without a Lifecycle Stage or an Engagement
      // Status, so a value nothing matched is answered "None" rather than left
      // blank. Blank would hold the step up over a choice the user has nothing
      // to make — the option list holds only what Worklap already knows.
      targetValue: matched || CONTACT_IMPORT_NO_VALUE,
      // Still records that the import found nothing for this value, so the row
      // can say so even now that it carries an answer.
      notFound: !matched,
    };
  });

  // Naming the first few values made the line grow with the file and say
  // nothing the rows above it do not already show.
  const note =
    values.length > 0
      ? "Each mapping applies to all matching rows automatically. Unresolved values above will fail validation in the next step."
      : undefined;

  const unresolvedValuesCount =
    column.totalUnresolvedCount ?? (column.unresolvedValues?.length ?? 0);
  const notFoundValuesCount = values.filter((v) => v.notFound).length;
  const moreNotFoundCount =
    unresolvedValuesCount > notFoundValuesCount
      ? unresolvedValuesCount - notFoundValuesCount
      : undefined;

  return {
    key: column.field,
    label: column.label,
    kind: "DROPDOWN",
    uniqueValueCount: column.totalValueCount ?? values.length,
    hasMorePages: hasFurtherPages(column.totalValuePages),
    placeholder: "Select an option",
    options,
    values,
    moreNotFoundCount,
    note,
    isFromApi: true,
  };
};

